import { HilPackageFamily } from '@cadstrom/backend/shared';
import { Editor } from '@monaco-editor/react';
import SaveIcon from '@mui/icons-material/Save';
import Button from '@mui/material/Button';
import { useColorScheme } from '@mui/material/styles';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import isEqual from 'lodash.isequal';
import { ZodJsonSchema } from '@cadstrom/cadstrom-shared';

import { useTRPC } from '#src/trpc.js';
import { useAutoSave } from '#utils/use-auto-save/use-auto-save.js';

import type { OnMount } from '@monaco-editor/react';
import type { json } from 'monaco-editor';

const fileMatch = 'UpdatePackageFamilyInputDto.json';

export const Route = createFileRoute('/_authorization-gate/package-store/$packageFamilyId/raw/')({
	staticData: {
		navigation: {
			label: 'Family: Raw',
			keywords: ['raw', 'json'],
		},
	},
	loader: async ({ context: { queryClient, getPackageFamilyOptions } }) => {
		const { pkgFamily } = await queryClient.ensureQueryData(getPackageFamilyOptions);
		return { pkgFamily };
	},
	head: ({ loaderData }) => ({
		meta: [{ title: (loaderData ? `Family ${loaderData.pkgFamily.name}: ` : '') + 'Raw • Package Editor • Cadstrom' }],
	}),
	component: PackageFamilyEditorRawTab,
});

function PackageFamilyEditorRawTab() {
	const queryClient = useQueryClient();
	const trpc = useTRPC();

	const { colorScheme } = useColorScheme();
	const { getPackageFamilyOptions } = Route.useRouteContext();

	const {
		data: { pkgFamily },
	} = useSuspenseQuery(getPackageFamilyOptions);

	const { mutateAsync: updatePackageFamily, isPending: updatePackageFamilyIsPending } = useMutation(
		trpc.packageFamily.update.mutationOptions({
			onSuccess: (res) => {
				queryClient.setQueryData(getPackageFamilyOptions.queryKey, res);

				setServerJson(stringify(res.pkgFamily));
			},
		}),
	);

	const {
		draftState: draftJson,
		setDraftState: setDraftJson,
		setServerState: setServerJson,
		isDirty,
		isValid,
		handleSave,
		saveAreaContainerRef,
	} = useAutoSave({
		initialState: stringify(pkgFamily),
		mutationKeys: trpc.packageFamily.update.mutationKey(),
		isValid: (draftState) => {
			try {
				return HilPackageFamily.schema.parse(JSON.parse(draftState));
			} catch {
				return false;
			}
		},
		saveFn: async (_draftData, _serverData, validationResult) => {
			await updatePackageFamily({
				id: pkgFamily.id,
				data: {
					isReviewed: validationResult.isReviewed,
					...(validationResult.name !== pkgFamily.name ? { name: validationResult.name } : {}),
				},
			});
		},
		equalityFn: (serverData, draftData) => {
			try {
				return isEqual(JSON.parse(serverData), JSON.parse(draftData));
			} catch {
				return false;
			}
		},
	});

	const handleMount: OnMount = (editor, monaco) => {
		const existingSchemas =
			// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- There's an error in the types of @monaco-editor/react
			(monaco.languages.json.jsonDefaults.diagnosticsOptions.schemas as json.DiagnosticsOptions['schemas']) ?? [];

		const schemas: json.DiagnosticsOptions['schemas'] = [
			// Filter out if we've already added this schema
			...existingSchemas.filter((schema) => !schema.fileMatch?.some((match) => match.includes(fileMatch))),
			{
				uri: `cadstrom://model/${fileMatch}`, // Make up a URI because Monaco needs one
				schema: ZodJsonSchema.fromSchema(HilPackageFamily.schema),
				fileMatch: [`*/${fileMatch}`],
			},
		];

		// eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- There's an error in the types of @monaco-editor/react
		monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
			// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
			...monaco.languages.json.jsonDefaults.diagnosticsOptions,
			schemas,
		});
	};

	return (
		<div ref={saveAreaContainerRef} className='relative grow'>
			<Editor
				width='100%'
				height='100%'
				language='json'
				value={draftJson}
				theme={colorScheme === 'dark' ? 'vs-dark' : 'light'}
				options={{
					folding: true,
				}}
				onMount={handleMount}
				onChange={(value) => setDraftJson(value ?? '')}
				path={`${pkgFamily.id}/${fileMatch}`}
			/>
			<Button
				disabled={!isValid || !isDirty}
				loading={updatePackageFamilyIsPending}
				startIcon={<SaveIcon />}
				className='absolute top-3 right-6 z-10'
				onClick={() => void handleSave()}
			>
				<p>Save</p>
			</Button>

			<p className='absolute right-0 bottom-0 z-10 bg-background px-1 py-0.5 text-xs text-gray-11 opacity-50 select-none'>
				F1 for editor options
			</p>
		</div>
	);
}

const stringify = (obj: object) => JSON.stringify(obj, undefined, '\t');
